import { useState } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Calculator, Droplets, FlaskConical, Syringe, Loader2 } from "lucide-react";
import AppLogo from "@/components/AppLogo";

export default function CalculatorPage() {
  const [peptideMg, setPeptideMg] = useState("");
  const [waterMl, setWaterMl] = useState("");
  const [desiredDose, setDesiredDose] = useState("");
  const [desiredDoseUnit, setDesiredDoseUnit] = useState("mg");
  const [results, setResults] = useState(null);
  const [calculating, setCalculating] = useState(false);

  const handleCalculate = async () => {
    if (!peptideMg || !waterMl || !desiredDose) {
      toast.error("Please fill in all fields");
      return;
    }

    setCalculating(true);
    try {
      const res = await api.post("/calculator/reconstitution", {
        peptide_mg: parseFloat(peptideMg),
        water_ml: parseFloat(waterMl),
        desired_dose: parseFloat(desiredDose),
        desired_dose_unit: desiredDoseUnit
      });
      setResults(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Calculation error");
    } finally {
      setCalculating(false);
    }
  };

  const handleReset = () => {
    setPeptideMg("");
    setWaterMl("");
    setDesiredDose("");
    setDesiredDoseUnit("mg");
    setResults(null);
  };

  return (
    <div className="page-container" data-testid="calculator-page">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <AppLogo size="small" showText={false} />
            <div>
              <h1 className="font-heading text-lg font-bold">Calculator</h1>
              <p className="text-xs text-muted-foreground">U100 insulin syringe</p>
            </div>
          </div>
          {results && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="rounded-full"
              data-testid="calc-reset-btn"
            >
              Reset
            </Button>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="p-4 space-y-6">
        {/* Inputs */}
        <Card className="animate-fadeIn">
          <CardHeader className="pb-4">
            <CardTitle className="text-base flex items-center gap-2">
              <FlaskConical size={18} className="text-primary" />
              Enter Values
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Peptide Amount */}
            <div className="space-y-2">
              <Label htmlFor="peptide" className="flex items-center gap-2">
                <FlaskConical size={14} />
                Peptide in Vial
              </Label>
              <div className="relative">
                <Input
                  id="peptide"
                  type="number"
                  step="any"
                  placeholder="5"
                  value={peptideMg}
                  onChange={(e) => setPeptideMg(e.target.value)}
                  className="h-14 text-lg pr-12"
                  data-testid="calc-peptide-input"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground font-mono">
                  mg
                </span>
              </div>
            </div>

            {/* Water Amount */}
            <div className="space-y-2">
              <Label htmlFor="water" className="flex items-center gap-2">
                <Droplets size={14} />
                Bacteriostatic Water Added
              </Label>
              <div className="relative">
                <Input
                  id="water"
                  type="number"
                  step="any"
                  placeholder="2"
                  value={waterMl}
                  onChange={(e) => setWaterMl(e.target.value)}
                  className="h-14 text-lg pr-12"
                  data-testid="calc-water-input"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground font-mono">
                  ml
                </span>
              </div>
            </div>

            {/* Desired Dose */}
            <div className="space-y-2">
              <Label htmlFor="dose" className="flex items-center gap-2">
                <Syringe size={14} />
                Desired Dose
              </Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="dose"
                    type="number"
                    step="any"
                    placeholder="250"
                    value={desiredDose}
                    onChange={(e) => setDesiredDose(e.target.value)}
                    className="h-14 text-lg"
                    data-testid="calc-dose-input"
                  />
                </div>
                <Select value={desiredDoseUnit} onValueChange={setDesiredDoseUnit}>
                  <SelectTrigger className="w-24 h-14" data-testid="calc-dose-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mcg">mcg</SelectItem>
                    <SelectItem value="mg">mg</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button
              onClick={handleCalculate}
              disabled={calculating}
              className="w-full h-14 rounded-full text-lg font-semibold"
              data-testid="calc-calculate-btn"
            >
              {calculating ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Calculating...
                </>
              ) : (
                <>
                  <Calculator className="mr-2 h-5 w-5" />
                  Calculate
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Results */}
        {results && (
          <Card className="animate-fadeIn border-primary/30" style={{ animationDelay: "0.1s", opacity: 0 }}>
            <CardHeader className="pb-4">
              <CardTitle className="text-base flex items-center gap-2">
                <Syringe size={18} className="text-primary" />
                Results
              </CardTitle>
              <CardDescription>
                Based on U100 insulin syringe (100 units = 1 ml)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Main Result - Units Needed */}
              <div className="calc-display">
                <div className="text-sm text-muted-foreground mb-1 text-left">Draw up</div>
                <div className="flex items-baseline justify-end gap-2">
                  <span className="text-4xl font-bold text-primary font-heading">
                    {results.units_needed}
                  </span>
                  <span className="text-xl text-muted-foreground">units</span>
                </div>
              </div>

              {/* Other Results */}
              <div className="grid grid-cols-2 gap-3">
                <div className="calc-result">
                  <span className="calc-result-label">Concentration</span>
                  <span className="calc-result-value">{results.concentration_mg_ml}</span>
                  <span className="text-xs text-muted-foreground">mg/ml</span>
                </div>

                <div className="calc-result">
                  <span className="calc-result-label">Per Unit</span>
                  <span className="calc-result-value">{results.mcg_per_unit}</span>
                  <span className="text-xs text-muted-foreground">mcg/unit</span>
                </div>

                <div className="calc-result col-span-2">
                  <span className="calc-result-label">Volume to Draw</span>
                  <span className="calc-result-value">{results.ml_needed}</span>
                  <span className="text-xs text-muted-foreground">ml</span>
                </div>
              </div>

              {/* Info Note */}
              <p className="text-xs text-muted-foreground text-center pt-2 border-t border-border/50">
                Always double-check calculations before administration
              </p>
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        {!results && (
          <Card className="animate-fadeIn bg-muted/50" style={{ animationDelay: "0.1s", opacity: 0 }}>
            <CardContent className="p-4">
              <h4 className="font-semibold text-sm mb-2">How it works</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>1. Enter the peptide amount in your vial (mg)</li>
                <li>2. Enter how much BAC water you added (ml)</li>
                <li>3. Enter your desired dose (mcg or mg)</li>
                <li>4. Get the exact units to draw on your syringe</li>
              </ul>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
